import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { useWizard } from 'ui-components/components/wizard/context';

import style from './style.module.less';
import { Providers } from '../../../../../enums/providers';
import { useFsuiContext } from '../../../../../financial-services-ui/fsui-context-provider';
import { postWalletRegistrationSuccess } from '../../../../../store/wallet/wallet.actions';
import { messages } from '../../../../messages';
import { PAYMENT_TAB, FORM_REQUIREMENTS, STAMPS_BAD_CREDENTIALS_REGEX } from '../../../../utilities/constants';
import { useWalletRegistrationErrorContext } from '../../contexts/wallet-registration-error-context';
import { useWalletRegistrationFormContext } from '../../contexts/wallet-registration-form-context';
import { useWalletRegistrationModalContext } from '../../contexts/wallet-registration-modal-context';
import { usePrepareSubmissionData } from '../../hooks/use-prepare-submission-data';

/**
 * Button that registers the wallet without adding funds (new flow only).
 * In the new flow (no terms tab), this button handles wallet registration
 * and then closes the modal.
 * @param {object} props
 * @param {boolean} [props.disabled]
 * @returns {React.JSX.Element}
 */
export const FundLaterButton = ({ disabled = false }) => {
  const dispatch = useDispatch();
  const { jumpToStep } = useWizard();
  const { setErrors } = useWalletRegistrationErrorContext();
  const { walletForm, submitRegistration, submittingRegistration, postWalletRegistrationSuccessPayload } =
    useWalletRegistrationFormContext();
  const { closeWalletModal, shouldShowAdyenDropInComponent, shouldShowOldTermsTab, walletToRegister } = useWalletRegistrationModalContext();
  const { createPaymentSession } = useFsuiContext();
  const { prepareSubmissionData } = usePrepareSubmissionData();

  // Check if UPS terms are accepted (only in new flow - old flow handles terms in terms tab)
  const { newLabelProviderIds = [] } = walletToRegister;
  const upsTermsAccepted = walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.UPS_TERMS];
  const isUpsWalletEligible = newLabelProviderIds.includes(Providers.UPSWallet.labelProviderId);
  const needsUpsTermsAcceptance = !shouldShowOldTermsTab && isUpsWalletEligible && !upsTermsAccepted;

  const handleClick = useCallback(async () => {
    // Old flow: wallet already registered on Terms tab, just close modal
    if (shouldShowOldTermsTab) {
      dispatch(postWalletRegistrationSuccess());
      closeWalletModal();
      return;
    }

    // New flow: register wallet first, then close modal
    setErrors([]);

    const { submissionData } = prepareSubmissionData();
    const { errors } = await submitRegistration(submissionData, false);

    if (errors) {
      // Refresh providers/wallets in case registration was partially successful
      dispatch(postWalletRegistrationSuccess(postWalletRegistrationSuccessPayload));
      setErrors(errors);

      const { Message = '' } = errors[0] || {};
      if (Message.match(STAMPS_BAD_CREDENTIALS_REGEX)) {
        jumpToStep(PAYMENT_TAB);
        return;
      }

      if (shouldShowAdyenDropInComponent) {
        createPaymentSession(true);
      }
      jumpToStep(PAYMENT_TAB);
      return;
    }
    dispatch(postWalletRegistrationSuccess());
    closeWalletModal();
  }, [
    shouldShowOldTermsTab,
    setErrors,
    prepareSubmissionData,
    submitRegistration,
    shouldShowAdyenDropInComponent,
    createPaymentSession,
    jumpToStep,
    dispatch,
    closeWalletModal,
    postWalletRegistrationSuccessPayload,
  ]);

  return (
    <Button
      className={style.button}
      disabled={disabled || submittingRegistration || needsUpsTermsAcceptance}
      disabledTooltipMessage={
        needsUpsTermsAcceptance ? <Text textType={TextType.Paragraph} value={messages.fundLaterTooltip} /> : undefined
      }
      onClick={handleClick}
      theme='secondary'
    >
      <FormattedMessage {...messages.fundLater} />
    </Button>
  );
};
